import React from 'react';
import { MapPin, Users } from 'react-feather';
import {
  InlineShell,
  InlineEyebrow,
  CardButton,
  formatEventDateTime,
  type F8CardData,
  type OnAction,
} from './cardHelpers';

interface CircleEvent {
  id?: string;
  eventId?: string;
  title?: string;
  description?: string;
  date?: any;
  dateTime?: any;
  location?: string;
  circleId?: string;
  rsvpCount?: number;
  hasRsvped?: boolean;
}

function EventRow({ event, onAction }: { event: CircleEvent; onAction?: OnAction }) {
  const id = event.eventId || event.id;
  const circleId = event.circleId;
  const title = event.title || 'Untitled event';
  const desc = event.description || '';
  const when = formatEventDateTime(event.date || event.dateTime);
  const isPast = (() => {
    const d = event.date || event.dateTime;
    if (!d) return false;
    const t = d?.toDate ? d.toDate() : new Date(d);
    return !isNaN(t.getTime()) && t.getTime() < Date.now();
  })();

  return (
    <div className={`f8c-event-row${isPast ? ' f8c-event-row-past' : ''}`}>
      <div className="f8c-event-head">
        <div className="f8c-event-title">{title}</div>
        {when && <div className="f8c-event-when">{when}</div>}
      </div>

      {desc && <p className="f8c-event-desc">{desc}</p>}

      <div className="f8c-event-meta">
        {event.location && (
          <span className="f8c-event-meta-item">
            <MapPin size={12} />
            {event.location}
          </span>
        )}
        {event.rsvpCount !== undefined && (
          <span className="f8c-event-meta-item">
            <Users size={12} />
            {event.rsvpCount} going
          </span>
        )}
      </div>

      <div className="f8c-event-actions">
        {!isPast && !event.hasRsvped && (
          <CardButton
            primary
            small
            onClick={() =>
              onAction?.({
                action: 'cloud_function',
                params: {
                  functionName: 'rsvpCircleEventEurW',
                  args: { circleId, eventId: id },
                },
              })
            }
          >
            RSVP
          </CardButton>
        )}
        <CardButton
          small
          onClick={() =>
            onAction?.({
              action: 'navigate',
              params: { screen: 'CircleEventDetail', circleId, eventId: id },
            })
          }
        >
          View details
        </CardButton>
      </div>
    </div>
  );
}

export default function EventsListCard({
  card,
  onAction,
}: {
  card: F8CardData;
  onAction?: OnAction;
}) {
  const events: CircleEvent[] = card?.data?.events || [];
  if (events.length === 0) return null;

  return (
    <InlineShell>
      <InlineEyebrow label={`${events.length} events`} />
      <div className="f8c-event-list">
        {events.map((event, i) => (
          <EventRow key={event.eventId || event.id || i} event={event} onAction={onAction} />
        ))}
      </div>
    </InlineShell>
  );
}
